import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FragmentBase, FragmentInjectable } from '../fragment-base';
import { CreateIntegrationResponse } from '@services/dws/winery-integrations.service';
import { WSPaymentsService } from '@services/dws/ws-payments.service';
import { StripeConnectService } from '@services/dws/stripe-connect.service';
import { WineryIntegrationsService } from '@services/dws/winery-integrations.service';
import { TermsAndConditionsService, TermsResult } from '@services/terms-and-conditions.service';
import { AccountManagementDialogComponent } from '@web/app/pages/payments/account-management-dialog/account-management-dialog.component';
import { TermsAndConditionsComponent } from '@web/app/components/terms-and-conditions/terms-and-conditions.component';
import { observe } from '@web/util/loading/loading';
import { toast } from '@web/util/toast';
import { tr } from '@util/tr';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'web-dwspay-connection',
  templateUrl: './dwspay-connection.component.html',
  styleUrls: ['./dwspay-connection.component.scss'],
})
export class DwspayConnectionComponent extends FragmentBase implements OnInit {
  private destroy$ = new Subject<void>();

  constructor(
    private injector: FragmentInjectable,
    private dialog: MatDialog,
    private wsPaymentsService: WSPaymentsService,
    private stripeConnectService: StripeConnectService,
    private wineryIntegrationsService: WineryIntegrationsService,
    private termsService: TermsAndConditionsService
  ) { 
    super(injector);
  }

  ngOnInit() {}

  get isLinked() {
    return !!this.wineryIntegration &&
    this.wineryIntegration.connected &&
    this.wineryIntegration.integrationData;
  }

  public get connectEnabled() {
    return !this.isLinked;
  }

  public get removeEnabled() {
    return this.isLinked;
  }

  public async connect() {
    // For install flow - open onboarding popup
    await this.openOnboardingPopup();
    // Return empty object to prevent parent from saving (we handle it in the popup)
    return {};
  }

  public async modify() {
    // For modify flow - open update config popup
    await this.openUpdateConfigPopup();
  }

  clickHandler(action: 'connect' | 'sync' | 'remove' | 'modify') {
    if (action === 'modify') {
      this.modify();
      return;
    }
    this.clickEventSubject.next({
      type: DwspayConnectionComponent,
      index: this.index,
      action: action as 'connect' | 'sync' | 'remove'
    });
  }

  public connectCallback(response: CreateIntegrationResponse) {
    // This callback is called after connect() completes
    // Since we handle everything in the popup, we don't need to do anything here
  }

  private async openOnboardingPopup() {
    try {
      // Show policy dialog first
      await this.showPolicyDialog();
    } catch (error) {
      console.error("Error opening onboarding popup:", error);
      toast(tr("Failed to start onboarding. Please try again."));
    }
  }

  private async showPolicyDialog() {
    // Get ws-payment type agreements
    observe(this.termsService.getTerms('ws-payment'))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: TermsResult) => {
          if (result.success && result.agreementsInfoDto && result.agreementsInfoDto.length > 0) {
            // Open terms and conditions dialog with ws-payment agreements
            const dialogRef = this.dialog.open(TermsAndConditionsComponent, {
              width: '90%',
              maxWidth: '1200px',
              maxHeight: '90vh',
              disableClose: true,
              data: result
            });

            dialogRef.afterClosed().subscribe((accepted: any) => {
              if (accepted) {
                this.startDwspayOnboarding();
              }
            });
          } else {
            // No agreements found, proceed directly
            this.startDwspayOnboarding();
          }
        },
        error: (error) => {
          console.error("Error loading agreements:", error);
          toast(tr("Failed to load agreements. Please try again."));
        }
      });
  }

  private async startDwspayOnboarding() {
    if (!this.wineryId) {
      toast(tr('Company information not available'));
      return;
    }
    
    try {
      // Account configuration according to Stripe API guide
      const accountConfiguration = {
        controller: {
          fees: { payer: "application" },
          stripe_dashboard: { type: "none" },
          losses: { payments: "application" },
          requirement_collection: "stripe"
        },
        capabilities: {
          transfers: { requested: true },
          card_issuing: { requested: true },
          card_payments: { requested: true }
        }
      };

      let accountId: string | undefined;
      let clientSecret: string;
      let publishableKey: string;

      // Check if integration exists but is disabled (resume onboarding)
      const existingIntegration = this.wineryIntegration;
      if (existingIntegration && existingIntegration.disabled) {
        // Resume onboarding with existing account
        accountId = existingIntegration.integrationData?.['stripe_account_connect_id'];
        
        if (!accountId) {
          throw new Error("Account ID not found in integration data");
        }

        console.log(`Resuming onboarding for existing account: ${accountId}`);

        // Resume onboarding by creating account session for existing account
        // IMPORTANT: Pass the accountId so Stripe resumes with the same account
        const sessionData = await observe(
          this.wsPaymentsService.resumeDwspayOnboarding(this.wineryId, accountId, accountConfiguration)
        ).toPromise();

        if (!sessionData?.clientSecret) {
          throw new Error("Failed to resume dwspay onboarding");
        }

        clientSecret = sessionData.clientSecret;
        publishableKey = sessionData.publishableKey || await this.getPublishableKey() || '';
      } else {
        // Create new account (integration doesn't exist)
        const accountData = await observe(
          this.wsPaymentsService.createDwspayAccount(this.wineryId, accountConfiguration)
        ).toPromise();

        if (!accountData?.accountId || !accountData?.clientSecret) {
          throw new Error("Failed to create dwspay account");
        }

        accountId = accountData.accountId;
        clientSecret = accountData.clientSecret;
        publishableKey = accountData.publishableKey || await this.getPublishableKey() || '';

        // Save account ID to integrations at first creation
        await observe(
          this.wineryIntegrationsService.createDwspayIntegration(this.wineryId, accountId)
        ).toPromise();
      }

      if (!publishableKey) {
        throw new Error("Stripe publishable key not available");
      }

      const fetchClientSecret = async (): Promise<string> => {
        return clientSecret;
      };

      // Set return URL - don't include onboarding_complete as Stripe uses this URL for both completion and abandonment
      // We'll verify the real state by checking the backend when dialog closes
      const returnUrl = `${window.location.origin}${window.location.pathname}`;
      
      // Initialize Stripe Connect for onboarding using embedded account onboarding component
      const stripeConnectInstance = await this.stripeConnectService.init(
        publishableKey,
        fetchClientSecret,
        {
          overlays: 'dialog',
          variables: {
            colorPrimary: "#003b71",
            colorText: "#222222",
            colorSecondaryText: "#717171",
            buttonBorderRadius: "11px",
            spacingUnit: "8px",
            fontFamily: "Poppins",
            colorBackground: "#ffffff",
            offsetBackgroundColor: "#ffffff",
            formBackgroundColor: "#ffffff"
          },
        }
      );
      
      // Open dialog - the onboarding component will be created inside the dialog
      const dialogRef = this.dialog.open(AccountManagementDialogComponent, {
        width: '90%',
        maxWidth: '1200px',
        height: '90%',
        maxHeight: '800px',
        data: {
          stripeConnectInstance: stripeConnectInstance,
          isOnboarding: true,
          title: tr('Connect Stripe Account'),
          returnUrl: returnUrl
        }
      });

      dialogRef.afterClosed().subscribe(() => {
        // After onboarding dialog closes, close the parent dialog
        this.closeParentDialog();
      });
      
    } catch (error) {
      console.error("Error starting dwspay onboarding:", error);
      toast(tr("Failed to start onboarding. Please try again."));
    }
  }

  private async openUpdateConfigPopup() {
    if (!this.wineryId) {
      toast(tr('Company information not available'));
      return;
    }

    try {
      // Get Stripe publishable key
      const publishableKey = await this.getPublishableKey();
      if (!publishableKey) {
        throw new Error("Stripe publishable key not available");
      }

      // Create fetchClientSecret function as per Stripe documentation
      const fetchClientSecret = async (): Promise<string> => {
        try {
          // Enable required components: account_management, payments, payouts, payment_disputes, and issuing_cards_list
          const accountSession = await observe(
            this.wsPaymentsService.createAccountSession(this.wineryId, {
              account_management: { enabled: true },
              payments: {
                enabled: true,
                features: {
                  refund_management: true,
                  dispute_management: true,
                  capture_payments: true
                }
              },
              payouts_list: {
                enabled: true,
                features: {
                  instant_payouts: true,
                  standard_payouts: true,
                  edit_payout_schedule: true,
                  external_account_collection: true
                }
              },
              payment_disputes: {
                enabled: true,
                features: {
                  refund_management: true,
                  dispute_management: true,
                  destination_on_behalf_of_charge_management: false
                }
              },
              issuing_cards_list: {
                enabled: true,
                features: {
                  card_management: true,
                  cardholder_management: true,
                  card_spend_dispute_management: true,
                  spend_control_management: true
                }
              }
            })
          ).toPromise() as { clientSecret: string; publishableKey?: string } | undefined;

          if (!accountSession?.clientSecret) {
            throw new Error("Failed to create account session");
          }

          return accountSession.clientSecret;
        } catch (error) {
          console.error("Error fetching client secret:", error);
          throw error;
        }
      };

      // Initialize Stripe Connect instance
      const stripeConnectInstance = await this.stripeConnectService.init(
        publishableKey,
        fetchClientSecret,
        {
          overlays: 'dialog',
          variables: {
            colorPrimary: "#003b71",
            colorText: "#222222",
            colorSecondaryText: "#717171",
            buttonBorderRadius: "11px",
            spacingUnit: "8px",
            fontFamily: "Poppins",
            colorBackground: "#ffffff",
            offsetBackgroundColor: "#ffffff",
            formBackgroundColor: "#ffffff"
          },
        }
      );

      // Open dialog with account-management component (update config)
      const dialogRef = this.dialog.open(AccountManagementDialogComponent, {
        width: '90%',
        maxWidth: '1200px',
        height: '90%',
        maxHeight: '800px',
        data: {
          stripeConnectInstance: stripeConnectInstance,
          bundleItem: 'upgrade',
          title: tr('Upgrade Config')
        }
      });

      dialogRef.afterClosed().subscribe(() => {
        // After update config dialog closes, close the parent dialog
        this.closeParentDialog();
      });

    } catch (error) {
      console.error("Error opening update config popup:", error);
      toast(tr("Failed to open update config. Please try again."));
    }
  }

  private async getPublishableKey(): Promise<string | null> {
    // Try to get from backend first (from account session response)
    try {
      const accountSession = await observe(
        this.wsPaymentsService.createAccountSession(this.wineryId, {
          account_management: { enabled: true }
        })
      ).toPromise() as { clientSecret: string; publishableKey?: string } | undefined;

      if (accountSession?.publishableKey) {
        return accountSession.publishableKey;
      }
    } catch (error) {
      console.warn("Could not fetch publishable key from backend:", error);
    }

    // Fallback to environment configuration
    return null;
  }

  private closeParentDialog() {
    // Close parent dialog if available (passed through FragmentInjectable)
    if (this.parentDialogRef) {
      this.parentDialogRef.close();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

